import type { FC, ReactNode } from 'react'

import { Download, FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLocale } from '@/i18n/use-locale'
import { siteContent } from '@/i18n/site-content'

interface ArticleReportProps {
    href: string
    className?: string
}

/** "Download the report" card for insights that carry a report file. */
export const ArticleReport: FC<ArticleReportProps> = ({ href, className }): ReactNode => {
    const locale = useLocale()
    const copy = siteContent[locale].article

    return (
        <div className={cn('relative overflow-hidden rounded-3xl bg-ink p-7 text-white', className)}>
            <div className='bg-grid-dark mask-radial pointer-events-none absolute inset-0 opacity-60' aria-hidden='true' />
            <div className='pointer-events-none absolute -right-16 -top-16 size-48 rounded-full bg-signal/20 blur-3xl' aria-hidden='true' />

            <div className='relative'>
                <span className='flex size-11 items-center justify-center rounded-2xl border border-white/15 bg-white/5 text-signal'>
                    <FileText className='size-5' aria-hidden='true' />
                </span>

                <p className='mt-5 font-display text-[1.7rem] leading-tight'>
                    {copy.reportTitle}
                </p>

                <p className='mt-2 text-sm leading-relaxed text-white/70'>
                    {copy.reportText}
                </p>

                <a
                    href={href}
                    target='_blank'
                    rel='noopener noreferrer'
                    download
                    className='mt-6 inline-flex h-11 items-center gap-2 rounded-full bg-white px-5 text-sm font-medium text-ink transition-colors hover:bg-white/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal'
                >
                    <Download className='size-4' aria-hidden='true' />
                    {copy.downloadReport}
                </a>
            </div>
        </div>
    )
}
